'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ProgressBar from '@/components/tools/ProgressBar';

/* ------------------------------------------------------------------ */
/*  Salary data: realistic 2024-2025 US base salary ranges (annual)   */
/*  p25 / p50 / p75 / p90 for mid-level (3-5 yrs) in a neutral market */
/* ------------------------------------------------------------------ */

interface RoleSalary {
  label: string;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
}

const ROLES: Record<string, RoleSalary> = {
  software_engineer:       { label: 'Software Engineer',            p25: 95000,  p50: 120000, p75: 155000, p90: 190000 },
  senior_software_engineer:{ label: 'Senior Software Engineer',     p25: 130000, p50: 160000, p75: 195000, p90: 235000 },
  frontend_developer:      { label: 'Frontend Developer',           p25: 85000,  p50: 110000, p75: 140000, p90: 170000 },
  backend_developer:       { label: 'Backend Developer',            p25: 90000,  p50: 115000, p75: 150000, p90: 185000 },
  fullstack_developer:     { label: 'Full-Stack Developer',         p25: 90000,  p50: 118000, p75: 150000, p90: 185000 },
  data_scientist:          { label: 'Data Scientist',               p25: 95000,  p50: 125000, p75: 160000, p90: 195000 },
  data_analyst:            { label: 'Data Analyst',                 p25: 60000,  p50: 78000,  p75: 95000,  p90: 115000 },
  data_engineer:           { label: 'Data Engineer',                p25: 100000, p50: 130000, p75: 165000, p90: 200000 },
  ml_engineer:             { label: 'Machine Learning Engineer',    p25: 115000, p50: 145000, p75: 185000, p90: 225000 },
  devops_engineer:         { label: 'DevOps / SRE Engineer',        p25: 100000, p50: 130000, p75: 165000, p90: 200000 },
  product_manager:         { label: 'Product Manager',              p25: 100000, p50: 130000, p75: 165000, p90: 200000 },
  project_manager:         { label: 'Project Manager',              p25: 75000,  p50: 95000,  p75: 120000, p90: 145000 },
  ux_designer:             { label: 'UX Designer',                  p25: 80000,  p50: 105000, p75: 130000, p90: 160000 },
  ui_designer:             { label: 'UI Designer',                  p25: 72000,  p50: 92000,  p75: 115000, p90: 140000 },
  graphic_designer:        { label: 'Graphic Designer',             p25: 48000,  p50: 60000,  p75: 75000,  p90: 92000  },
  marketing_manager:       { label: 'Marketing Manager',            p25: 72000,  p50: 95000,  p75: 125000, p90: 155000 },
  digital_marketing:       { label: 'Digital Marketing Specialist', p25: 52000,  p50: 68000,  p75: 88000,  p90: 110000 },
  content_strategist:      { label: 'Content Strategist',           p25: 58000,  p50: 75000,  p75: 95000,  p90: 115000 },
  sales_representative:    { label: 'Sales Representative',         p25: 50000,  p50: 65000,  p75: 85000,  p90: 110000 },
  account_executive:       { label: 'Account Executive',            p25: 65000,  p50: 85000,  p75: 115000, p90: 150000 },
  financial_analyst:       { label: 'Financial Analyst',            p25: 65000,  p50: 82000,  p75: 100000, p90: 125000 },
  accountant:              { label: 'Accountant',                   p25: 55000,  p50: 70000,  p75: 88000,  p90: 108000 },
  hr_manager:              { label: 'HR Manager',                   p25: 75000,  p50: 95000,  p75: 120000, p90: 145000 },
  recruiter:               { label: 'Recruiter',                    p25: 52000,  p50: 68000,  p75: 88000,  p90: 110000 },
  registered_nurse:        { label: 'Registered Nurse',             p25: 62000,  p50: 78000,  p75: 95000,  p90: 115000 },
  teacher:                 { label: 'Teacher (K-12)',               p25: 42000,  p50: 55000,  p75: 68000,  p90: 82000  },
  mechanical_engineer:     { label: 'Mechanical Engineer',          p25: 72000,  p50: 90000,  p75: 112000, p90: 135000 },
  electrical_engineer:     { label: 'Electrical Engineer',          p25: 75000,  p50: 95000,  p75: 118000, p90: 142000 },
  civil_engineer:          { label: 'Civil Engineer',               p25: 68000,  p50: 85000,  p75: 105000, p90: 128000 },
  pharmacist:              { label: 'Pharmacist',                   p25: 115000, p50: 130000, p75: 145000, p90: 160000 },
  business_analyst:        { label: 'Business Analyst',             p25: 68000,  p50: 85000,  p75: 108000, p90: 130000 },
  operations_manager:      { label: 'Operations Manager',           p25: 70000,  p50: 90000,  p75: 115000, p90: 140000 },
  customer_success:        { label: 'Customer Success Manager',     p25: 60000,  p50: 78000,  p75: 100000, p90: 125000 },
  technical_writer:        { label: 'Technical Writer',             p25: 62000,  p50: 78000,  p75: 98000,  p90: 120000 },
  cybersecurity_analyst:   { label: 'Cybersecurity Analyst',        p25: 85000,  p50: 110000, p75: 140000, p90: 170000 },
  cloud_architect:         { label: 'Cloud / Solutions Architect',  p25: 130000, p50: 160000, p75: 195000, p90: 235000 },
};

const LOCATION_TYPES = [
  { value: 'major_metro',  label: 'Major Metro (NYC, SF, LA, Chicago, Seattle, Boston)' },
  { value: 'mid_city',     label: 'Mid-Size City (Austin, Denver, Nashville, Raleigh)' },
  { value: 'small_rural',  label: 'Small City / Rural' },
  { value: 'remote',       label: 'Remote (location-independent)' },
] as const;

const INDUSTRIES = [
  'Technology / Software',
  'Finance / Banking',
  'Healthcare',
  'Manufacturing',
  'Retail / E-commerce',
  'Consulting',
  'Education',
  'Government',
  'Media / Entertainment',
  'Energy / Utilities',
  'Nonprofit',
  'Other',
] as const;

const EDUCATION_LEVELS = [
  { value: 'high_school',  label: 'High School / GED' },
  { value: 'associate',    label: "Associate's Degree" },
  { value: 'bachelor',     label: "Bachelor's Degree" },
  { value: 'master',       label: "Master's Degree" },
  { value: 'mba',          label: 'MBA' },
  { value: 'doctorate',    label: 'Doctorate / PhD' },
  { value: 'professional', label: 'Professional Degree (JD, MD)' },
] as const;

/* ------------------------------------------------------------------ */
/*  Adjustment multipliers                                             */
/* ------------------------------------------------------------------ */

function locationMultiplier(loc: string): number {
  switch (loc) {
    case 'major_metro': return 1.18;
    case 'mid_city':    return 1.0;
    case 'small_rural': return 0.87;
    case 'remote':      return 1.05;
    default:            return 1.0;
  }
}

function experienceMultiplier(years: number): number {
  // The base data is calibrated for ~3-5 years.
  if (years <= 1)  return 0.78;
  if (years <= 2)  return 0.86;
  if (years <= 3)  return 0.93;
  if (years <= 5)  return 1.0;
  if (years <= 8)  return 1.10;
  if (years <= 12) return 1.20;
  if (years <= 15) return 1.28;
  return 1.35; // 16+
}

function educationMultiplier(edu: string): number {
  switch (edu) {
    case 'high_school':  return 0.92;
    case 'associate':    return 0.96;
    case 'bachelor':     return 1.0;
    case 'master':       return 1.06;
    case 'mba':          return 1.10;
    case 'doctorate':    return 1.12;
    case 'professional': return 1.14;
    default:             return 1.0;
  }
}

function industryMultiplier(ind: string): number {
  switch (ind) {
    case 'Technology / Software': return 1.08;
    case 'Finance / Banking':     return 1.10;
    case 'Healthcare':            return 1.0;
    case 'Consulting':            return 1.06;
    case 'Energy / Utilities':    return 1.04;
    case 'Manufacturing':         return 0.97;
    case 'Retail / E-commerce':   return 0.95;
    case 'Education':             return 0.90;
    case 'Government':            return 0.93;
    case 'Nonprofit':             return 0.88;
    case 'Media / Entertainment': return 0.98;
    default:                      return 1.0;
  }
}

/* ------------------------------------------------------------------ */
/*  Result types                                                       */
/* ------------------------------------------------------------------ */

type Verdict =
  | 'Significantly Underpaid'
  | 'Slightly Below Market'
  | 'At Market Rate'
  | 'Above Market'
  | 'Well Above Market';

interface AnalysisResult {
  adjustedP25: number;
  adjustedP50: number;
  adjustedP75: number;
  adjustedP90: number;
  percentile: number;
  verdict: Verdict;
  verdictColor: string;
  factors: string[];
  tips: string[];
}

/* ------------------------------------------------------------------ */
/*  Core analysis function                                             */
/* ------------------------------------------------------------------ */

function analyze(
  roleKey: string,
  years: number,
  location: string,
  industry: string,
  education: string,
  salary: number,
): AnalysisResult {
  const role = ROLES[roleKey];

  const locM = locationMultiplier(location);
  const expM = experienceMultiplier(years);
  const eduM = educationMultiplier(education);
  const indM = industryMultiplier(industry);

  const adj = (base: number) => Math.round(base * locM * expM * eduM * indM);
  const adjustedP25 = adj(role.p25);
  const adjustedP50 = adj(role.p50);
  const adjustedP75 = adj(role.p75);
  const adjustedP90 = adj(role.p90);

  // Calculate percentile (linear interpolation)
  let percentile: number;
  if (salary <= adjustedP25) {
    percentile = Math.max(1, Math.round((salary / adjustedP25) * 25));
  } else if (salary <= adjustedP50) {
    percentile = 25 + Math.round(((salary - adjustedP25) / (adjustedP50 - adjustedP25)) * 25);
  } else if (salary <= adjustedP75) {
    percentile = 50 + Math.round(((salary - adjustedP50) / (adjustedP75 - adjustedP50)) * 25);
  } else if (salary <= adjustedP90) {
    percentile = 75 + Math.round(((salary - adjustedP75) / (adjustedP90 - adjustedP75)) * 15);
  } else {
    percentile = Math.min(99, 90 + Math.round(((salary - adjustedP90) / adjustedP90) * 50));
  }

  // Verdict
  let verdict: Verdict;
  let verdictColor: string;
  if (percentile < 20) {
    verdict = 'Significantly Underpaid';
    verdictColor = '#dc2626'; // red-600
  } else if (percentile < 40) {
    verdict = 'Slightly Below Market';
    verdictColor = '#f59e0b'; // amber-500
  } else if (percentile < 65) {
    verdict = 'At Market Rate';
    verdictColor = '#16a34a'; // green-600
  } else if (percentile < 85) {
    verdict = 'Above Market';
    verdictColor = '#2563eb'; // blue-600
  } else {
    verdict = 'Well Above Market';
    verdictColor = '#7c3aed'; // violet-600
  }

  // Explanatory factors
  const factors: string[] = [];
  if (locM > 1.05) factors.push('Your major-metro location typically commands higher salaries (+15-20%).');
  if (locM < 0.95) factors.push('Salaries in smaller cities or rural areas tend to be 10-15% lower than national medians.');
  if (location === 'remote') factors.push('Remote roles often pay 0-10% above the national median, depending on the employer.');
  if (expM > 1.15) factors.push(`With ${years}+ years of experience, you should expect above-median compensation.`);
  if (expM < 0.9) factors.push(`At ${years} year${years === 1 ? '' : 's'} of experience, your salary is expected to be below the mid-career median.`);
  if (eduM > 1.05) factors.push('Your advanced degree or professional credential typically adds a salary premium.');
  if (eduM < 0.96) factors.push('Candidates with a bachelor\'s degree or higher often earn more in this role.');
  if (indM > 1.05) factors.push(`The ${industry} industry tends to pay above average for this role.`);
  if (indM < 0.95) factors.push(`The ${industry} sector typically pays below the national average for this role.`);

  // Tips
  const tips: string[] = [];
  if (percentile < 40) {
    tips.push(
      'Research comparable job postings on sites like Levels.fyi, Glassdoor, and Payscale to confirm the market rate.',
      'Document your accomplishments and quantify your impact before starting a negotiation.',
      'Schedule a meeting with your manager specifically to discuss compensation -- don\'t bring it up casually.',
      'Consider the total compensation package: equity, bonuses, 401k match, health benefits, and PTO can add 20-40% on top of base salary.',
      'If your current employer won\'t budge, use outside offers as leverage or consider making a move -- job-switchers see 10-20% average salary increases.',
    );
  } else if (percentile < 65) {
    tips.push(
      'You are fairly compensated, but there may still be room to grow. Ask about the next salary band during your review.',
      'Focus on high-visibility projects that demonstrate your value to the organization.',
      'Negotiate for non-salary perks: remote flexibility, professional development budget, or extra PTO.',
    );
  } else {
    tips.push(
      'You are earning above market -- keep developing your skills to maintain that edge.',
      'Consider whether equity, bonuses, or other incentives could further grow your total compensation.',
      'Mentoring others and building leadership skills can open the door to even higher-paying roles.',
    );
  }

  return { adjustedP25, adjustedP50, adjustedP75, adjustedP90, percentile, verdict, verdictColor, factors, tips };
}

/* ------------------------------------------------------------------ */
/*  Formatting helpers                                                 */
/* ------------------------------------------------------------------ */

function fmt(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

/* ------------------------------------------------------------------ */
/*  Salary distribution bar component                                  */
/* ------------------------------------------------------------------ */

function SalaryDistribution({
  p25, p50, p75, p90, salary, verdictColor,
}: {
  p25: number; p50: number; p75: number; p90: number; salary: number; verdictColor: string;
}) {
  const min = p25 * 0.7;
  const max = p90 * 1.2;
  const range = max - min;
  const pct = (v: number) => Math.max(0, Math.min(100, ((v - min) / range) * 100));

  const zones = [
    { left: pct(min),  width: pct(p25) - pct(min),  color: 'bg-red-200',    label: 'Below 25th' },
    { left: pct(p25),  width: pct(p50) - pct(p25),  color: 'bg-amber-200',  label: '25th-50th' },
    { left: pct(p50),  width: pct(p75) - pct(p50),  color: 'bg-green-200',  label: '50th-75th' },
    { left: pct(p75),  width: pct(p90) - pct(p75),  color: 'bg-blue-200',   label: '75th-90th' },
    { left: pct(p90),  width: 100 - pct(p90),        color: 'bg-violet-200', label: 'Above 90th' },
  ];

  const markerLeft = pct(salary);

  return (
    <div className="w-full">
      {/* Bar */}
      <div className="relative h-10 rounded-full overflow-hidden flex">
        {zones.map((z, i) => (
          <div key={i} className={`${z.color} h-full`} style={{ width: `${z.width}%` }} />
        ))}
        {/* Salary marker */}
        <div
          className="absolute top-0 h-full w-1 -ml-0.5"
          style={{ left: `${markerLeft}%`, backgroundColor: verdictColor }}
        />
        <div
          className="absolute -top-7 -translate-x-1/2 text-xs font-bold whitespace-nowrap"
          style={{ left: `${markerLeft}%`, color: verdictColor }}
        >
          You: {fmt(salary)}
        </div>
      </div>

      {/* Percentile labels */}
      <div className="relative h-6 mt-1 text-[10px] text-gray-500 font-medium">
        <span className="absolute" style={{ left: `${pct(p25)}%`, transform: 'translateX(-50%)' }}>25th<br/>{fmt(p25)}</span>
        <span className="absolute" style={{ left: `${pct(p50)}%`, transform: 'translateX(-50%)' }}>50th<br/>{fmt(p50)}</span>
        <span className="absolute" style={{ left: `${pct(p75)}%`, transform: 'translateX(-50%)' }}>75th<br/>{fmt(p75)}</span>
        <span className="absolute" style={{ left: `${pct(p90)}%`, transform: 'translateX(-50%)' }}>90th<br/>{fmt(p90)}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function UnderpaidCalculatorContent() {
  const [roleKey, setRoleKey] = useState('software_engineer');
  const [years, setYears] = useState(5);
  const [location, setLocation] = useState('mid_city');
  const [industry, setIndustry] = useState<string>('Technology / Software');
  const [education, setEducation] = useState('bachelor');
  const [salary, setSalary] = useState<number | ''>('');
  const [result, setResult] = useState<AnalysisResult | null>(null);

  function handleCompare() {
    if (!salary || salary <= 0) return;
    setResult(analyze(roleKey, years, location, industry, education, salary as number));
  }

  const sortedRoles = Object.entries(ROLES).sort((a, b) => a[1].label.localeCompare(b[1].label));

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <ToolHero
        title='Am I Underpaid? Calculator'
        description="Compare your salary to market rates for your role, experience level, and location. Find out if you are being paid fairly."
        category="Salary Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* ---------- Input Form ---------- */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 text-lg font-extrabold">$</span>
            Your Compensation Details
          </h2>
          <p className="text-sm text-gray-500 mb-6">All calculations are performed locally in your browser. We never store or send your salary data.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Job Title */}
            <div>
              <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-2">
                Job Title
              </label>
              <select
                id="role"
                value={roleKey}
                onChange={(e) => setRoleKey(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition bg-white"
              >
                {sortedRoles.map(([key, r]) => (
                  <option key={key} value={key}>{r.label}</option>
                ))}
              </select>
            </div>

            {/* Years of Experience */}
            <div>
              <label htmlFor="years" className="block text-sm font-semibold text-gray-700 mb-2">
                Years of Experience: <span className="text-emerald-600 font-bold">{years}{years >= 20 ? '+' : ''}</span>
              </label>
              <input
                id="years"
                type="range"
                min={1}
                max={20}
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                className="w-full accent-emerald-600 h-2 rounded-lg cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1 yr</span>
                <span>10 yrs</span>
                <span>20+ yrs</span>
              </div>
            </div>

            {/* Location Type */}
            <div>
              <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                Location Type
              </label>
              <select
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition bg-white"
              >
                {LOCATION_TYPES.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
                Industry
              </label>
              <select
                id="industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition bg-white"
              >
                {INDUSTRIES.map((ind) => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>

            {/* Education Level */}
            <div>
              <label htmlFor="education" className="block text-sm font-semibold text-gray-700 mb-2">
                Education Level
              </label>
              <select
                id="education"
                value={education}
                onChange={(e) => setEducation(e.target.value)}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition bg-white"
              >
                {EDUCATION_LEVELS.map((e) => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </select>
            </div>

            {/* Current Salary */}
            <div>
              <label htmlFor="salary" className="block text-sm font-semibold text-gray-700 mb-2">
                Current Annual Base Salary
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  id="salary"
                  type="number"
                  placeholder="e.g. 95000"
                  value={salary}
                  onChange={(e) => setSalary(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-xl pl-8 pr-4 py-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Base salary only (exclude bonuses, equity, and benefits).</p>
            </div>
          </div>

          {/* Compare button */}
          <div className="mt-8">
            <button
              onClick={handleCompare}
              disabled={!salary || salary <= 0}
              className="w-full bg-gradient-to-r from-emerald-600 to-green-500 text-white font-bold py-4 px-8 rounded-xl shadow-lg hover:from-emerald-700 hover:to-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg cursor-pointer"
            >
              Compare My Salary
            </button>
          </div>
        </div>

        {/* ---------- Results ---------- */}
        {result && (
          <div className="mt-10 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Verdict Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <p className="text-sm uppercase tracking-widest text-gray-400 mb-2">Your Salary Verdict</p>
              <h3
                className="text-3xl sm:text-4xl font-extrabold mb-3"
                style={{ color: result.verdictColor }}
              >
                {result.verdict}
              </h3>
              <p className="text-gray-600 text-lg mb-6">
                Your salary of <span className="font-bold text-gray-900">{fmt(salary as number)}</span> puts you at
                approximately the <span className="font-bold" style={{ color: result.verdictColor }}>{result.percentile}th percentile</span> for
                {' '}{ROLES[roleKey].label}s in your market.
              </p>

              {/* Percentile gauge */}
              <div className="max-w-xs mx-auto">
                <ProgressBar value={result.percentile} label="Salary Percentile" />
              </div>
            </div>

            {/* Salary Distribution Visual */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Salary Distribution for {ROLES[roleKey].label}</h3>
              <div className="pt-8 pb-4 px-2">
                <SalaryDistribution
                  p25={result.adjustedP25}
                  p50={result.adjustedP50}
                  p75={result.adjustedP75}
                  p90={result.adjustedP90}
                  salary={salary as number}
                  verdictColor={result.verdictColor}
                />
              </div>

              {/* Legend */}
              <div className="flex flex-wrap justify-center gap-3 mt-8 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 inline-block" /> Below 25th</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200 inline-block" /> 25th - 50th</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 inline-block" /> 50th - 75th</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-200 inline-block" /> 75th - 90th</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-violet-200 inline-block" /> Above 90th</span>
              </div>
            </div>

            {/* Market Range Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Estimated Market Range for Your Profile</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: '25th Percentile', value: result.adjustedP25, color: 'text-amber-600' },
                  { label: 'Median (50th)',    value: result.adjustedP50, color: 'text-green-600' },
                  { label: '75th Percentile',  value: result.adjustedP75, color: 'text-blue-600' },
                  { label: '90th Percentile',  value: result.adjustedP90, color: 'text-violet-600' },
                ].map((item) => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className={`text-xl sm:text-2xl font-extrabold ${item.color}`}>{fmt(item.value)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-sm text-gray-600">
                  Fair market range for a <span className="font-semibold">{ROLES[roleKey].label}</span> with your profile:
                </p>
                <p className="text-2xl font-extrabold text-emerald-700 mt-1">
                  {fmt(result.adjustedP25)} &ndash; {fmt(result.adjustedP75)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Adjusted for your location, experience, education, and industry.</p>
              </div>
            </div>

            {/* Difference from median */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Your Salary vs. Median</h3>
              {(() => {
                const diff = (salary as number) - result.adjustedP50;
                const pctDiff = ((diff / result.adjustedP50) * 100).toFixed(1);
                const isUnder = diff < 0;
                return (
                  <div className={`flex items-center gap-4 p-6 rounded-xl ${isUnder ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
                    <div className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center text-2xl font-extrabold ${isUnder ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                      {isUnder ? '-' : '+'}
                    </div>
                    <div>
                      <p className={`text-2xl font-extrabold ${isUnder ? 'text-red-600' : 'text-green-600'}`}>
                        {fmt(Math.abs(diff))} ({isUnder ? '' : '+'}{pctDiff}%)
                      </p>
                      <p className="text-gray-600 text-sm">
                        {isUnder
                          ? `You are earning ${fmt(Math.abs(diff))} less than the adjusted median for this role.`
                          : `You are earning ${fmt(Math.abs(diff))} more than the adjusted median for this role.`}
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Factors */}
            {result.factors.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Factors Affecting Your Salary Position</h3>
                <ul className="space-y-3">
                  {result.factors.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-gray-700">
                      <span className="mt-1 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations & Tips */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {result.percentile < 40 ? 'How to Negotiate a Raise' : 'Next Steps'}
              </h3>
              <ul className="space-y-3">
                {result.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700">
                    <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">
                      {i + 1}
                    </span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Methodology */}
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-3">How This Calculator Works</h3>
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  We use aggregated salary data from public sources for 35+ common roles in the United States
                  (2024-2025). Base percentiles (25th, 50th, 75th, 90th) are then adjusted using four multipliers:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><span className="font-semibold text-gray-700">Location:</span> Major metro areas add ~18%, while small cities/rural areas reduce by ~13%.</li>
                  <li><span className="font-semibold text-gray-700">Experience:</span> Ranges from 0.78x for 1 year to 1.35x for 16+ years, calibrated around 3-5 year mid-career baseline.</li>
                  <li><span className="font-semibold text-gray-700">Education:</span> Advanced degrees add a 6-14% premium over a bachelor&apos;s degree baseline.</li>
                  <li><span className="font-semibold text-gray-700">Industry:</span> Sectors like finance and tech pay 8-10% above average; education and nonprofit pay 10-12% below.</li>
                </ul>
                <p>
                  This tool provides estimates for informational purposes only. Actual salaries vary based on company size,
                  specific skills, negotiation, equity, bonuses, and many other factors.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.bls.gov/oes/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics Occupational Employment &amp; Wage Statistics</a>
                  {' · '}
                  <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">BLS Occupational Outlook Handbook</a>
                  {' · '}
                  <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">O*NET OnLine &ndash; Occupation Profiles &amp; Wage Data</a>
                </p>
              </div>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Ready to negotiate your worth?"
              description="A polished, ATS-optimized resume gives you leverage in any salary conversation. Build yours in minutes with Superpower Resume."
              buttonText="Build My Resume Free"
            />
          </div>
        )}
      </div>
    </div>
  );
}
